import { memo, useMemo } from 'react';
import 'katex/dist/katex.min.css';
import MathText from '../MathText';
import { Fragment } from 'react';
import { preprocessBraces, buildMathHtml, enLignes, enChaine, enTableau } from '../texteTrouMath';

/**
 * Rendu CÔTÉ PROF d'une question "Texte à trous" (aperçu exerciseur, modale de
 * prévisualisation, import). L'espace élève ne passe pas par ici mais par
 * QuestionInteractive : les trous sont donc remplis avec la réponse attendue,
 * en vert, comme le font QCMRenderer et TexteLibreRenderer. Sans ça le prof
 * relit un exercice dont il ne voit pas le corrigé.
 *
 * Supporte deux modes de rendu :
 * - Phrase classique : texte brut avec MathText pour les fragments $...$
 * - Phrase KaTeX globale ($...$) : expression unique, réponses soulignées
 *
 * @param {string} phrase - La phrase avec {{trous}}
 * @param {string} mode - 'drag' ou 'saisie'
 * @param {Array} distracteurs - Les mots distracteurs (mode drag uniquement)
 * @param {boolean} light - Theme clair (fond blanc)
 */
const TexteTrouRenderer = memo(function TexteTrouRenderer({ phrase, mode, distracteurs, light }) {
    const { segments, answerWords, mathHtml } = useMemo(() => {
        if (!phrase) return { segments: [], answerWords: [], mathHtml: null };
        const safe = preprocessBraces(phrase);

        // Extraire les trous
        const parts = [];
        const answers = [];
        let lastIndex = 0;
        const re = /\{\{([^}]+)\}\}/g;
        let m;

        while ((m = re.exec(safe)) !== null) {
            if (m.index > lastIndex) {
                parts.push({ type: 'text', content: safe.substring(lastIndex, m.index) });
            }
            parts.push({ type: 'slot', content: m[1].trim() });
            answers.push(m[1].trim());
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < safe.length) {
            parts.push({ type: 'text', content: safe.substring(lastIndex) });
        }

        // Mode KaTeX global (équation entière). Ce renderer est exclusivement
        // côté prof (aperçu, modale, import) : on montre la réponse attendue
        // dans le trou, comme le font les autres renderers. Pas d'étai ici :
        // aucun champ n'est superposé, la barre du \dfrac n'est jamais masquée.
        // Le trou corrigé reprend le LANGAGE VISUEL des slots : valeur verte
        // sur POINTILLÉS (comme le mode texte, border-dashed emerald) — jamais
        // \underline : son trait PLEIN sous un numérateur se lit comme une
        // deuxième barre de fraction (constat utilisateur, gen_1005,
        // 2026-08-19). \htmlClass exige trust:true (déjà actif dans
        // buildMathHtml) ; le style vit sur le conteneur via [&_.tt-corrige].
        const renderedLines = buildMathHtml(
            phrase,
            (i) => `\\htmlClass{tt-corrige}{${answers[i] ?? ''}}`,
        );

        return { segments: parts, answerWords: answers, mathHtml: renderedLines };
    }, [phrase]);

    const shuffledWords = useMemo(() => {
        const all = [...answerWords, ...(distracteurs || [])];
        const shuffled = [...all];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [answerWords, distracteurs]);

    if (!phrase) return null;

    return (
        <div>
            <span className={`text-[10px] font-semibold uppercase tracking-wider mb-2 block ${light ? 'text-gray-400' : 'text-slate-400'}`}>
                Texte à trous{mode === 'saisie' ? ' (saisie)' : ''}
            </span>

            {/* Phrase avec trous */}
            {/* Interligne élargi quand la phrase contient une fraction verticale
                (\dfrac) : sur plusieurs lignes en mode texte, les fractions se
                chevauchaient (aperçu prof de gen_923, retour écran 2026-09-06 ;
                même règle que l'écran élève, TexteTrouEleve). */}
            <div className={`px-4 py-3 rounded-xl border text-sm ${/\\dfrac/.test(phrase) ? 'texte-trou--fractions' : 'leading-8'} ${
                light ? 'border-gray-200 bg-gray-50 text-gray-700' : 'border-slate-600 bg-slate-700/50 text-slate-200'
            }`}>
                {mathHtml ? (
                    /* Mode KaTeX global — corrigé dans les trous, style slot
                       (pointillés émeraude, mêmes teintes que le mode texte) */
                    <div
                        className={`[&_.katex]:text-inherit [&_.tt-corrige]:border-b-2 [&_.tt-corrige]:border-dashed [&_.tt-corrige]:px-0.5 [&_.tt-corrige]:font-semibold ${
                            light
                                ? '[&_.tt-corrige]:border-emerald-400 [&_.tt-corrige]:text-emerald-700'
                                : '[&_.tt-corrige]:border-emerald-500/60 [&_.tt-corrige]:text-emerald-300'
                        }`}
                        dangerouslySetInnerHTML={{ __html: mathHtml }}
                    />
                ) : (
                    /* Mode classique — une LIGNE de la phrase = un bloc
                       (enLignes, même règle que l'écran élève) ; une CHAÎNE
                       d'égalités (enChaine) : tête + membres « = … »
                       insécables, repli seulement au « = » (.tt-chaine) */
                    (() => {
                    const lignes = enLignes(segments);
                    const renduSeg = (seg, i) => (
                        seg.type === 'text' ? (
                            <MathText key={i}>{seg.content}</MathText>
                        ) : (
                            <span
                                key={i}
                                className={`inline-block min-w-[60px] mx-1 px-2 py-0.5 border-b-2 border-dashed text-center font-medium ${
                                    light ? 'border-emerald-400 text-emerald-700' : 'border-emerald-500/60 text-emerald-300'
                                }`}
                            >
                                <MathText>{seg.content}</MathText>
                            </span>
                        )
                    );
                    // toutes les lignes en « libellé : contenu » → deux colonnes alignées
                    const tableau = enTableau(lignes);
                    if (tableau) {
                        return (
                            <div className="tt-tableau">
                                {tableau.map(({ gauche, droite }, li) => (
                                    <Fragment key={li}>
                                        <span>{gauche.map(renduSeg)}</span>
                                        <span>{droite.map(renduSeg)}</span>
                                    </Fragment>
                                ))}
                            </div>
                        );
                    }
                    return lignes.map((ligne, li) => {
                        const chaine = enChaine(ligne);
                        if (!chaine) return <div key={li} className="tt-ligne">{ligne.map(renduSeg)}</div>;
                        return (
                            <div key={li} className="tt-ligne tt-chaine">
                                <span>{chaine.tete.map(renduSeg)}</span>
                                <span className="tt-chaine-membres">
                                    {chaine.membres.map((m, k) => <span key={k} className="tt-membre">{m.map(renduSeg)}</span>)}
                                </span>
                            </div>
                        );
                    });
                    })()
                )}
            </div>

            {/* Mots proposés — mode drag uniquement */}
            {mode === 'drag' && shuffledWords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {shuffledWords.map((word, i) => (
                        <span
                            key={i}
                            className={`px-3 py-1.5 rounded-lg border text-xs cursor-grab ${
                                light ? 'border-gray-200 bg-white text-gray-700' : 'border-slate-500 bg-slate-700 text-slate-200'
                            }`}
                        >
                            <MathText>{word}</MathText>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
});

export default TexteTrouRenderer;
